import { ready, type Sodium } from "./sodium";
import { hkdf } from "./keys";
import { concat, constantTimeEqual } from "./util";

/**
 * PSK-authenticated ephemeral ECDH — the Noise `NNpsk0` pattern.
 *
 * Both peers already share `psk` (derived from the link token, which the server
 * never sees). They exchange fresh ephemeral X25519 public keys, compute a
 * Diffie-Hellman shared secret, and mix `dh || psk` into HKDF to get directional
 * session keys plus key-confirmation tags.
 *
 *   - The DH gives forward secrecy + confidentiality vs. a passive snoop.
 *   - The psk authenticates the DH: a server/MITM that swapped the ephemeral
 *     keys cannot produce a valid confirmation tag (it has no psk), so the
 *     handshake fails closed.
 *
 * Run this OVER the WebRTC data channel so it does not depend on trusting DTLS
 * fingerprints relayed through the signaling server.
 */

export type Role = "initiator" | "responder";

export interface Session {
  /** AEAD key for messages this peer sends. */
  txKey: Uint8Array;
  /** AEAD key for messages this peer receives. */
  rxKey: Uint8Array;
  /** Short Authentication String — optional out-of-band human verification. */
  sas: string;
}

export class Handshake {
  private sodium!: Sodium;
  private ephSk!: Uint8Array;
  private ephPk!: Uint8Array;
  private myConfirm!: Uint8Array;
  private peerConfirm!: Uint8Array;
  private session!: Session;

  constructor(
    private readonly psk: Uint8Array,
    private readonly role: Role
  ) {}

  /** Generate our ephemeral keypair; returns the public half to send. */
  async init(): Promise<Uint8Array> {
    this.sodium = await ready();
    this.ephSk = this.sodium.randombytes_buf(32);
    this.ephPk = this.sodium.crypto_scalarmult_base(this.ephSk);
    return this.ephPk;
  }

  /**
   * Derive session keys + confirmation tags from the peer's ephemeral key.
   * Returns our confirmation tag, to be sent to the peer.
   */
  async derive(
    peerEphPk: Uint8Array
  ): Promise<{ session: Session; confirm: Uint8Array }> {
    const dh = this.sodium.crypto_scalarmult(this.ephSk, peerEphPk);

    // Canonical transcript ordering: initiator's key first, then responder's.
    const initPk = this.role === "initiator" ? this.ephPk : peerEphPk;
    const respPk = this.role === "initiator" ? peerEphPk : this.ephPk;

    const ikm = concat(dh, this.psk);
    const transcript = concat(initPk, respPk);
    const okm = await hkdf(
      ikm,
      transcript,
      "enclave/handshake/v1",
      32 + 32 + 32 + 8
    );

    const kI2R = okm.slice(0, 32);
    const kR2I = okm.slice(32, 64);
    const confirmKey = okm.slice(64, 96);
    const sasSeed = okm.slice(96, 104);

    const confirmI = await hkdf(
      confirmKey,
      transcript,
      "enclave/confirm/initiator",
      16
    );
    const confirmR = await hkdf(
      confirmKey,
      transcript,
      "enclave/confirm/responder",
      16
    );

    const isInitiator = this.role === "initiator";
    this.myConfirm = isInitiator ? confirmI : confirmR;
    this.peerConfirm = isInitiator ? confirmR : confirmI;
    this.session = {
      txKey: isInitiator ? kI2R : kR2I,
      rxKey: isInitiator ? kR2I : kI2R,
      sas: sasFromSeed(sasSeed),
    };

    this.sodium.memzero(dh);
    return { session: this.session, confirm: this.myConfirm };
  }

  /** Verify the peer's key-confirmation tag. Failure => abort (MITM/wrong link). */
  verifyPeer(peerConfirm: Uint8Array): boolean {
    return constantTimeEqual(peerConfirm, this.peerConfirm);
  }

  get result(): Session {
    return this.session;
  }
}

/** Derive a 6-digit Short Authentication String for optional human comparison. */
function sasFromSeed(seed: Uint8Array): string {
  const view = new DataView(seed.buffer, seed.byteOffset, seed.byteLength);
  const n = view.getUint32(0, false) % 1_000_000;
  const s = n.toString().padStart(6, "0");
  return `${s.slice(0, 3)} ${s.slice(3)}`;
}
